import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, Trash2, Upload, AlertCircle, EyeOff } from 'lucide-react';
import { ParkingLot } from '../lib/types';
import { createParkingLot, updateParkingLot, deleteParkingLot, getParkingLotByIdLegacy } from '../lib/api';
import { validatePolygon, GoogleMapsLatLng, convertGoogleMapsToGeoJSON } from '../lib/utils';
import GeoJSONImport from './GeoJSONImport';
import { GeoJSONImportResponse } from '../lib/api';

interface ParkingLotMapSidebarProps {
    selectedPlace: ParkingLot | null;
    editMode: boolean;
    creatingNew: boolean;
    currentCoordinates: GoogleMapsLatLng[];
    hiddenInMapView?: Set<string>;
    onClose: () => void;
    onSave: (place: ParkingLot) => void;
    onDelete: (placeId: string) => void;
    onCancel: () => void;
    onImportComplete: () => void;
    onHidePolygon?: (lotId: string) => void;
    onShowAllPolygons?: () => void;
    availableLots?: ParkingLot[];
}

const ParkingLotMapSidebar: React.FC<ParkingLotMapSidebarProps> = ({
    selectedPlace,
    editMode,
    creatingNew,
    currentCoordinates,
    hiddenInMapView,
    onClose,
    onSave,
    onDelete,
    onCancel,
    onImportComplete,
    onHidePolygon,
    onShowAllPolygons,
    availableLots = []
}) => {
    // Point/line lots can have metadata edited without converting their geometry to a polygon.
    const preservesExistingGeometry = !creatingNew && (
        selectedPlace?.geometry?.type === 'Point' ||
        selectedPlace?.geometry?.type === 'LineString' ||
        selectedPlace?.geometry?.type === 'MultiLineString'
    );

    const [showImport, setShowImport] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isComparing, setIsComparing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [compareLotId, setCompareLotId] = useState('');
    const [compareLot, setCompareLot] = useState<ParkingLot | null>(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        parking_type: 'free',
        owner: '',
        spaces: '',
        notes: '',
        lotId: '',
        displayInApp: true
    });

    // Initialize form data when selected place changes
    useEffect(() => {
        if (selectedPlace && !creatingNew) {
            setFormData({
                name: selectedPlace.name || '',
                address: selectedPlace.address || '',
                parking_type: selectedPlace.parking_type || 'free',
                owner: selectedPlace.owner || '',
                spaces: selectedPlace.spaces?.toString() || '',
                notes: selectedPlace.notes || '',
                lotId: selectedPlace.lotId || '',
                displayInApp: selectedPlace.displayInApp ?? true
            });
        } else if (creatingNew) {
            setFormData({
                name: '',
                address: '',
                parking_type: 'free',
                owner: '',
                spaces: '',
                notes: '',
                lotId: '',
                displayInApp: true
            });
        }
        setError(null);
        setSuccess(null);
        setCompareLot(null);
        setCompareLotId('');
    }, [selectedPlace, creatingNew]);

    const handleInputChange = (field: string, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError(null);
    };

    const handleSave = async () => {
        // Point/line lots skip polygon validation — their existing geometry is preserved as-is.
        if (!preservesExistingGeometry) {
            const validation = validatePolygon(currentCoordinates);
            if (!validation.isValid) {
                setError(validation.error || 'Ugyldig polygon');
                return;
            }
        }

        setIsSaving(true);
        setError(null);
        setSuccess(null);

        try {
            let geometry: ReturnType<typeof convertGoogleMapsToGeoJSON> | undefined;
            if (!preservesExistingGeometry) {
                geometry = convertGoogleMapsToGeoJSON(currentCoordinates);
                if (!geometry) {
                    throw new Error('Kunne ikke konvertere koordinater til GeoJSON');
                }
            }

            const parkingLotData: Partial<ParkingLot> = {
                name: formData.name.trim() || undefined,
                address: formData.address.trim() || undefined,
                parking_type: formData.parking_type,
                owner: formData.owner.trim() || undefined,
                spaces: formData.spaces ? parseInt(formData.spaces, 10) : undefined,
                notes: formData.notes.trim() || undefined,
                lotId: formData.lotId.trim() || undefined,
                displayInApp: formData.displayInApp,
                ...(geometry !== undefined ? { geometry: geometry as any } : {}),
            };

            let savedPlace: ParkingLot;
            if (creatingNew) {
                savedPlace = await createParkingLot(parkingLotData);
                setSuccess('Plads oprettet succesfuldt');
            } else if (selectedPlace) {
                savedPlace = await updateParkingLot(selectedPlace._id, parkingLotData);
                setSuccess('Plads opdateret succesfuldt');
            } else {
                throw new Error('Ingen plads valgt');
            }

            // Wait a bit to show success message
            setTimeout(() => {
                onSave(savedPlace);
            }, 1000);
        } catch (err: any) {
            setError(err.message || 'Fejl ved gemning af plads');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedPlace) return;

        if (!window.confirm(`Er du sikker på at du vil slette "${selectedPlace.name || selectedPlace.lotId}"?`)) {
            return;
        }

        setIsDeleting(true);
        setError(null);

        try {
            await deleteParkingLot(selectedPlace._id);
            setSuccess('Plads slettet succesfuldt');
            setTimeout(() => {
                onDelete(selectedPlace._id);
            }, 1000);
        } catch (err: any) {
            setError(err.message || 'Fejl ved sletning af plads');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleImportComplete = (result: GeoJSONImportResponse) => {
        setShowImport(false);
        onImportComplete();
    };

    const handleLoadCompareLot = async () => {
        const normalizedId = compareLotId.trim();
        if (!normalizedId) return;

        setIsComparing(true);
        setError(null);
        try {
            const targetId =
                availableLots.find((lot) => lot.lotId === normalizedId || lot._id === normalizedId)?._id ||
                normalizedId;
            const loaded = await getParkingLotByIdLegacy(targetId);
            setCompareLot(loaded);
        } catch (err: any) {
            setError(err.message || 'Kunne ikke hente sammenligningsplads');
        } finally {
            setIsComparing(false);
        }
    };

    const draftGeometry = useMemo(() => {
        if (!editMode || currentCoordinates.length === 0) return null;
        return convertGoogleMapsToGeoJSON(currentCoordinates);
    }, [editMode, currentCoordinates]);

    const draftPayload = useMemo(() => {
        if (!editMode) return null;
        return {
            ...(selectedPlace || {}),
            ...formData,
            spaces: formData.spaces ? parseInt(formData.spaces, 10) : undefined,
            geometry: draftGeometry || selectedPlace?.geometry || null,
        };
    }, [draftGeometry, editMode, formData, selectedPlace]);

    const splitGeometry = (value: unknown) => {
        if (!value || typeof value !== 'object') {
            return { core: value, geometry: null };
        }
        const { geometry, ...rest } = value as Record<string, unknown>;
        return { core: rest, geometry: geometry ?? null };
    };

    const selectedJsonParts = useMemo(() => splitGeometry(selectedPlace), [selectedPlace]);
    const draftJsonParts = useMemo(() => splitGeometry(draftPayload), [draftPayload]);
    const compareJsonParts = useMemo(() => splitGeometry(compareLot), [compareLot]);

    const readableSelectedPlaceJson = useMemo(
        () => (selectedJsonParts.core ? JSON.stringify(selectedJsonParts.core, null, 2) : null),
        [selectedJsonParts]
    );

    const readableDraftJson = useMemo(
        () => (draftJsonParts.core ? JSON.stringify(draftJsonParts.core, null, 2) : null),
        [draftJsonParts]
    );

    const readableCompareLotJson = useMemo(
        () => (compareJsonParts.core ? JSON.stringify(compareJsonParts.core, null, 2) : null),
        [compareJsonParts]
    );

    const compareDiffRows = useMemo(() => {
        if (!selectedPlace || !compareLot) return [];
        const fields = [
            'lotId',
            'name',
            'address',
            'parking_type',
            'owner',
            'spaces',
            'notes',
            'restriction_description',
            'displayInApp',
        ] as const;

        return fields.map((field) => {
            const left = selectedPlace[field];
            const right = compareLot[field];
            return {
                field,
                left: left == null ? '' : String(left),
                right: right == null ? '' : String(right),
                different: JSON.stringify(left ?? null) !== JSON.stringify(right ?? null),
            };
        });
    }, [compareLot, selectedPlace]);

    const polygonInfo = editMode && currentCoordinates.length > 0 ? (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <p className="text-sm font-medium text-blue-900">Polygon Information</p>
            <p className="text-xs text-blue-700 mt-1">
                {currentCoordinates.length} {currentCoordinates.length === 1 ? 'punkt' : 'punkter'}
                {currentCoordinates.length < 3 && (
                    <span className="text-red-600 ml-2">
                        (Minimum 3 punkter påkrævet)
                    </span>
                )}
            </p>
        </div>
    ) : null;

    if (showImport) {
        return (
            <div className="h-full overflow-y-auto">
                <GeoJSONImport
                    onImportComplete={handleImportComplete}
                    onClose={() => setShowImport(false)}
                />
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-white border-l border-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                    {creatingNew ? 'Opret Ny Plads' : selectedPlace ? 'Rediger Plads' : 'Plads Detaljer'}
                </h2>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                >
                    <X className="h-5 w-5" />
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Hide polygon on map (session-only) */}
                {!editMode && selectedPlace && !creatingNew && onHidePolygon && selectedPlace.lotId && (
                    <div className="flex flex-col gap-2">
                        {hiddenInMapView?.has(selectedPlace.lotId) ? (
                            <p className="text-sm text-gray-500">Denne polygon er skjult på kortet.</p>
                        ) : (
                            <button
                                type="button"
                                onClick={() => onHidePolygon(selectedPlace.lotId)}
                                className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium"
                            >
                                <EyeOff className="h-4 w-4" />
                                Skjul polygon på kort
                            </button>
                        )}
                        {hiddenInMapView && hiddenInMapView.size > 0 && onShowAllPolygons && (
                            <button
                                type="button"
                                onClick={onShowAllPolygons}
                                className="w-full py-2 px-3 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-600 text-sm"
                            >
                                Vis alle polygoner
                            </button>
                        )}
                    </div>
                )}

                {/* Polygon Info */}
                {polygonInfo}

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-800 flex-1">{error}</p>
                    </div>
                )}

                {/* Success Message */}
                {success && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm text-green-800">{success}</p>
                    </div>
                )}

                {/* Form */}
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Navn <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="F.eks. Sauers Plads"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Lot ID
                        </label>
                        <input
                            type="text"
                            value={formData.lotId}
                            onChange={(e) => handleInputChange('lotId', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="F.eks. aal_1 (genereres automatisk hvis tom)"
                            disabled={!creatingNew}
                        />
                        {!creatingNew && (
                            <p className="text-xs text-gray-500 mt-1">Lot ID kan ikke ændres</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Adresse
                        </label>
                        <input
                            type="text"
                            value={formData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="F.eks. Vendsysselgade 11, 9000 Aalborg"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Parkerings Type
                        </label>
                        <select
                            value={formData.parking_type}
                            onChange={(e) => handleInputChange('parking_type', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="free">Gratis</option>
                            <option value="paid">Betalt</option>
                            <option value="limited_time">Tidsbegrænset</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Ejer
                        </label>
                        <input
                            type="text"
                            value={formData.owner}
                            onChange={(e) => handleInputChange('owner', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="F.eks. Kommunal off. tilgængelig"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Antal Pladser
                        </label>
                        <input
                            type="number"
                            value={formData.spaces}
                            onChange={(e) => handleInputChange('spaces', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="F.eks. 10"
                            min="0"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Noter
                        </label>
                        <textarea
                            value={formData.notes}
                            onChange={(e) => handleInputChange('notes', e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Yderligere noter..."
                            rows={3}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="displayInApp"
                            checked={formData.displayInApp}
                            onChange={(e) => handleInputChange('displayInApp', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="displayInApp" className="text-sm font-medium text-gray-700">
                            Synlig i app
                        </label>
                    </div>
                    <p className="text-xs text-gray-500 -mt-2">
                        Når afkrydset er fjernet, udelades pladsen fra den offentlige app (liste og detalje).
                    </p>
                </div>

                {selectedPlace && (
                    <div className="border-t border-gray-200 pt-4">
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Sammenlign med anden plads</h3>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={compareLotId}
                                onChange={(e) => setCompareLotId(e.target.value)}
                                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Indtast lotId eller _id"
                            />
                            <button
                                type="button"
                                onClick={() => void handleLoadCompareLot()}
                                disabled={isComparing || !compareLotId.trim()}
                                className="rounded-md bg-slate-800 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                            >
                                {isComparing ? 'Henter...' : 'Sammenlign'}
                            </button>
                        </div>

                        {compareLot && (
                            <div className="mt-3 rounded-lg border border-gray-200">
                                <div className="border-b border-gray-200 px-3 py-2 text-sm font-medium text-gray-900">
                                    Diff: {selectedPlace.lotId} vs {compareLot.lotId}
                                </div>
                                <div className="max-h-72 overflow-auto">
                                    <table className="min-w-full text-xs">
                                        <thead className="sticky top-0 bg-gray-50 text-gray-600">
                                            <tr>
                                                <th className="px-3 py-2 text-left font-medium">Felt</th>
                                                <th className="px-3 py-2 text-left font-medium">{selectedPlace.lotId}</th>
                                                <th className="px-3 py-2 text-left font-medium">{compareLot.lotId}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {compareDiffRows.map((row) => (
                                                <tr key={row.field} className={row.different ? 'bg-amber-50' : ''}>
                                                    <td className="px-3 py-2 font-medium text-gray-700">{row.field}</td>
                                                    <td className="px-3 py-2 align-top text-gray-900">{row.left || '—'}</td>
                                                    <td className="px-3 py-2 align-top text-gray-900">{row.right || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Readable JSON inspector for map-based review/editing */}
                <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">JSON data</h3>

                    {editMode && readableDraftJson && (
                        <details open className="mb-3 rounded-lg border border-blue-200 bg-blue-50/40">
                            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-blue-900">
                                Aktuel draft JSON
                            </summary>
                            <div className="border-t border-blue-200 px-3 py-3">
                                <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-md bg-slate-950 p-3 text-xs leading-5 text-slate-100">
                                    {readableDraftJson}
                                </pre>
                                {draftJsonParts.geometry && (
                                    <details className="mt-3 rounded-md border border-slate-700 bg-slate-900/60">
                                        <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-slate-200">
                                            Geometry
                                        </summary>
                                        <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-all border-t border-slate-700 p-3 text-xs leading-5 text-slate-300">
                                            {JSON.stringify(draftJsonParts.geometry, null, 2)}
                                        </pre>
                                    </details>
                                )}
                            </div>
                        </details>
                    )}

                    {readableSelectedPlaceJson && (
                        <details className="rounded-lg border border-gray-200 bg-gray-50">
                            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-gray-900">
                                Original lot JSON
                            </summary>
                            <div className="border-t border-gray-200 px-3 py-3">
                                <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-md bg-slate-950 p-3 text-xs leading-5 text-slate-100">
                                    {readableSelectedPlaceJson}
                                </pre>
                                {selectedJsonParts.geometry && (
                                    <details className="mt-3 rounded-md border border-slate-700 bg-slate-900/60">
                                        <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-slate-200">
                                            Geometry
                                        </summary>
                                        <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-all border-t border-slate-700 p-3 text-xs leading-5 text-slate-300">
                                            {JSON.stringify(selectedJsonParts.geometry, null, 2)}
                                        </pre>
                                    </details>
                                )}
                            </div>
                        </details>
                    )}

                    {compareLot && readableCompareLotJson && (
                        <details className="mt-3 rounded-lg border border-gray-200 bg-gray-50">
                            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-gray-900">
                                Sammenlignings-lot JSON
                            </summary>
                            <div className="border-t border-gray-200 px-3 py-3">
                                <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-md bg-slate-950 p-3 text-xs leading-5 text-slate-100">
                                    {readableCompareLotJson}
                                </pre>
                                {compareJsonParts.geometry && (
                                    <details className="mt-3 rounded-md border border-slate-700 bg-slate-900/60">
                                        <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-slate-200">
                                            Geometry
                                        </summary>
                                        <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-all border-t border-slate-700 p-3 text-xs leading-5 text-slate-300">
                                            {JSON.stringify(compareJsonParts.geometry, null, 2)}
                                        </pre>
                                    </details>
                                )}
                            </div>
                        </details>
                    )}

                    {!readableSelectedPlaceJson && !readableDraftJson && (
                        <p className="text-xs text-gray-500">
                            Vælg eller opret en plads for at se JSON-data her.
                        </p>
                    )}
                </div>
            </div>

            {/* Footer Actions */}
            <div className="border-t border-gray-200 p-4 space-y-2">
                {editMode && (
                    <>
                        <button
                            onClick={handleSave}
                            disabled={isSaving || (!preservesExistingGeometry && currentCoordinates.length < 3)}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSaving ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Gemmer...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4 mr-2" />
                                    Gem
                                </>
                            )}
                        </button>
                        <button
                            onClick={onCancel}
                            disabled={isSaving}
                            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md text-sm font-medium"
                        >
                            Annuller
                        </button>
                    </>
                )}

                {!editMode && selectedPlace && (
                    <button
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isDeleting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Sletter...
                            </>
                        ) : (
                            <>
                                <Trash2 className="h-4 w-4 mr-2" />
                                Slet Plads
                            </>
                        )}
                    </button>
                )}

                {!editMode && (
                    <button
                        onClick={() => setShowImport(true)}
                        className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center"
                    >
                        <Upload className="h-4 w-4 mr-2" />
                        Import GeoJSON
                    </button>
                )}
            </div>
        </div>
    );
};

export default ParkingLotMapSidebar;
